// Which currency a screen SHOWS an amount in, as opposed to the one it is
// stored in.
//
// Deals are CAD or USD and the billing screens carry both, often in adjacent
// rows. Two things follow, and this module owns both.
//
// FIRST: an amount on screen has to say which dollar it is. `$1,250.00` is
// ambiguous here in a way it is not in a single-currency app, so the currency is
// part of the figure — either spelled on the amount itself or, in a dense table,
// carried in a column of its own that every money cell in the row belongs to.
//
// SECOND: sometimes a reader genuinely does want one number. "What is the whole
// quarter worth in CAD" is a fair question that a per-currency breakdown does
// not answer. So the screen can be switched into a target currency — and when
// it is, this module converts by the SAME rule the server's reports use
// (`api/src/reports/fx.ts`):
//
//   - the rate applied is the latest one effective on or before the row's OWN
//     date, not today's rate and not the newest row of the table. That is the
//     entire point of a dated rate table: a bill due in March is worth what it
//     was worth in March, whatever someone enters tomorrow.
//   - a date the table cannot cover is NOT converted at 1.0. It stays in its own
//     currency and says so. A USD figure silently relabelled CAD is out by a
//     third and looks completely plausible — the worst thing this screen could
//     do.
//   - the rate that was actually applied is echoed back to the reader, so any
//     figure can be reproduced by hand.
//
// Conversion is display only. Nothing here is ever written back: the rows keep
// their own `currency`, and the CSV export is in source currency for that
// reason.

import { z } from "zod";
import { convertMinor, type Currency, type FxApplied, type FxRate } from "@crm/shared";
import { formatCalendarDate } from "./calendarDate.js";
import { formatMoney, formatRate, type FormatMoneyOptions } from "./money.js";

/**
 * `natural` keeps every row in its own currency — the default, and the only
 * mode in which the figures on screen are exact. The other two convert.
 */
export const DisplayCurrency = z.enum(["natural", "CAD", "USD"]);
export type DisplayCurrency = z.infer<typeof DisplayCurrency>;

/** One amount, ready to render, and honest about how it got that way. */
export type DisplayAmount =
  /** Written in this currency to begin with. Exact. */
  | { kind: "native"; minor: number; currency: Currency }
  /** Converted at a dated rate. Approximate, and carries its provenance. */
  | {
      kind: "converted";
      minor: number;
      currency: Currency;
      source: { minor: number; currency: Currency };
      rate: FxApplied;
    }
  /** Wanted converting, but no rate was in force on its date. Left alone. */
  | { kind: "unconvertible"; minor: number; currency: Currency; target: Currency };

export interface CurrencyView {
  mode: DisplayCurrency;
  /** The one currency everything is shown in, or null when each row keeps its own. */
  target: Currency | null;
  amount: (minor: number, from: Currency, onDate: string) => DisplayAmount;
}

/**
 * The rate in force on `onDate`: the latest row with `effective_date <= onDate`,
 * or null when the table does not reach back that far.
 *
 * ISO dates compare lexicographically in calendar order, so no `Date` is built
 * and no reader's timezone can move a business date by a day. Order-independent
 * on purpose — the API hands these over newest-first and a caller should not
 * have to know that.
 */
export function pickRate(rates: readonly FxRate[], onDate: string): FxRate | null {
  let winner: FxRate | null = null;
  for (const rate of rates) {
    if (rate.effective_date > onDate) continue;
    if (winner === null || rate.effective_date > winner.effective_date) winner = rate;
  }
  return winner;
}

export function currencyView(mode: DisplayCurrency, rates: readonly FxRate[]): CurrencyView {
  const target = mode === "natural" ? null : mode;

  return {
    mode,
    target,
    amount: (minor, from, onDate) => {
      if (target === null || from === target) return { kind: "native", minor, currency: from };

      const rate = pickRate(rates, onDate);
      if (rate === null) return { kind: "unconvertible", minor, currency: from, target };

      return {
        kind: "converted",
        minor: convertMinor(minor, from, target, rate.usd_to_cad),
        currency: target,
        source: { minor, currency: from },
        rate: {
          target_currency: target,
          usd_to_cad: rate.usd_to_cad,
          as_of: rate.effective_date,
        },
      };
    },
  };
}

/** A row, reduced to the two facts that decide whether it needs converting. */
export interface DatedAmount {
  currency: Currency;
  /** The row's own business date — a due date, not the day the page was opened. */
  date: string;
}

export interface ConversionSummary {
  /**
   * The most recent rate that was actually APPLIED, or null when nothing needed
   * converting. Null and "the newest rate in the table" are different claims:
   * naming a rate that touched no figure on screen is worse than naming none.
   */
  fx: FxApplied | null;
  /** Rows left in their own currency because no rate covered their date. */
  unconvertible: number;
}

/**
 * What the footnote under a converted screen has to say, computed from the rows
 * on screen rather than from the rate table — so it describes the figures the
 * reader is actually looking at.
 */
export function summariseConversion(
  view: CurrencyView,
  rows: Iterable<DatedAmount>,
): ConversionSummary {
  let fx: FxApplied | null = null;
  let unconvertible = 0;

  for (const row of rows) {
    const amount = view.amount(0, row.currency, row.date);
    if (amount.kind === "unconvertible") unconvertible += 1;
    else if (amount.kind === "converted" && (fx === null || amount.rate.as_of > fx.as_of)) {
      fx = amount.rate;
    }
  }

  return { fx, unconvertible };
}

/**
 * A converted figure is prefixed `≈`. It is rounded to the cent from a rate with
 * six decimal places, so it will not tie out against an invoice to the penny —
 * and a reader comparing this screen to one has to be able to see that at a
 * glance rather than discover it in a reconciliation.
 */
export function formatDisplayAmount(
  amount: DisplayAmount,
  options: FormatMoneyOptions = {},
): string {
  const text = formatMoney(amount.minor, amount.currency, options);
  return amount.kind === "converted" ? `≈${text}` : text;
}

/** The `title` on a money cell: where the figure came from, in one sentence. */
export function describeDisplayAmount(amount: DisplayAmount): string | undefined {
  switch (amount.kind) {
    case "native":
      return undefined;
    case "converted":
      return `${formatMoney(amount.source.minor, amount.source.currency)}, converted at USD→CAD ${formatRate(amount.rate.usd_to_cad)} as of ${formatCalendarDate(amount.rate.as_of)}.`;
    case "unconvertible":
      return `Shown in ${amount.currency}: no FX rate is effective on or before this row's date, so it was not converted to ${amount.target}.`;
  }
}
